/**
 * useCostingGeneration — the commercial costing generation and pricing seam.
 *
 * TS-060 (DEC-COUNCIL-015): `handleGenerateProjectCostingSheet` and
 * `handleSetBuilderHbcfDecision` moved
 * VERBATIM from App.jsx behind a deps object. The hook owns NO pipeline state —
 * every state value, setter, ref, and helper threads in from App.jsx per the
 * useQuoteEditor pattern.
 */

import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { BusinessPricingService } from '../services/pricing';
import {
    assertCanonicalHbcfDecisionReceipt,
    claimHbcfDecisionSaveOwner,
} from '../services/hbcf-decision-transaction';
import { managedScopeItemIncludesHbcf } from '../services/rj-bird-pricing-methodology';
import { bindGeneratedQuoteToCurrentScope } from '../utils/project-stage';
import { jobPricingMethodology, reconcileStatutoryRowsOnScopeBasis } from '../utils/stage4-pricing';
import { bindWmsToScopeProjection, hasFreshWmsScopeProjection } from '../utils/stage3-work-method';
import {
    captureProjectRunContext,
    createStaleProjectRunError,
    isProjectRunContextCurrent,
} from '../utils/project-run-context';
import { formatCostingRefreshFailure } from '../utils/costing-review-message';
import { fallbackBusinessName } from '../services/demo-identity';
import { resolveAssignedQmQuoteNo } from '../utils/assigned-qm-quote-no';
import { isBrainProject } from '../utils/quote-brain-project';
import { repriceBrainScope } from '../services/quote-brain-pricing';

export interface UseCostingGenerationDeps {
    scope: any[];
    scopeGstInclusive: boolean;
    formData: any;
    projectWMS: any;
    projectStrategy: any;
    workMethodOverallProgramme: any;
    analysis: any;
    quoteData: any;
    projectCostingSheet: any;
    projectDefaultMargin?: number;
    quoteEditorOpen: boolean;
    quoteEditorLiveSnapshot: any;
    currentProjectId: string | null;
    businessPricingSnapshotStatus: string;
    currentBusinessPricingSnapshot: any;
    /** Live Pricing ('business-training') access. Only an explicit false gates. */
    canPersistInternalPricing?: boolean;

    setScope: Dispatch<SetStateAction<any>>;
    setFormData: Dispatch<SetStateAction<any>>;
    setProjectWMS: Dispatch<SetStateAction<any>>;
    setQuoteData: Dispatch<SetStateAction<any>>;
    setProjectCostingSheet: Dispatch<SetStateAction<any>>;
    setToast: Dispatch<SetStateAction<any>>;
    setCostingRegenerating: Dispatch<SetStateAction<boolean>>;
    setQuoteEditorLiveSnapshot: Dispatch<SetStateAction<any>>;

    projectWMSRef: MutableRefObject<any>;
    projectCostingSheetRef: MutableRefObject<any>;
    scopeRef: MutableRefObject<any[]>;
    quoteDataRef: MutableRefObject<any>;
    currentProjectIdRef: { current: any };
    projectLoadGenerationRef: { current: any };
    projectAbortRef: MutableRefObject<AbortController>;
    costingGenerationOwnerRef: MutableRefObject<number>;
    hbcfDecisionSaveOwnerRef: MutableRefObject<any>;

    isSavingProjectRef: { current: any };
    pendingSaveRef: { current: any };
    saveDrainTimerRef: { current: any };

    saveCurrentProject: (payload: any, options?: any) => Promise<any>;
    isCurrentProjectQuoteGenerationInFlight: () => boolean;
}

export function useCostingGeneration(deps: UseCostingGenerationDeps) {
    const {
        scope,
        scopeGstInclusive,
        formData,
        projectWMS,
        projectStrategy,
        workMethodOverallProgramme,
        analysis,
        quoteData,
        projectCostingSheet,
        projectDefaultMargin,
        quoteEditorOpen,
        quoteEditorLiveSnapshot,
        currentProjectId,
        businessPricingSnapshotStatus,
        currentBusinessPricingSnapshot,
        canPersistInternalPricing,

        setScope,
        setFormData,
        setProjectWMS,
        setQuoteData,
        setProjectCostingSheet,
        setToast,
        setCostingRegenerating,
        setQuoteEditorLiveSnapshot,

        projectWMSRef,
        projectCostingSheetRef,
        scopeRef,
        quoteDataRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        projectAbortRef,
        costingGenerationOwnerRef,
        hbcfDecisionSaveOwnerRef,

        isSavingProjectRef,
        pendingSaveRef,
        saveDrainTimerRef,

        saveCurrentProject,
        isCurrentProjectQuoteGenerationInFlight,
    } = deps;

    // W4: a brain project's rows priced again by pricing-core after a change
    // to the list (src/services/quote-brain-pricing.ts). It saves only when an
    // amount moved, says what moved, and never builds an old costing sheet.
    const priceBrainProjectWithPricingCore = async ({ scope: brainScope, quoteDataOverride: quoteOverride = null, silent: quiet = false, runContext: givenContext = null, expectedProjectId: brainProjectId = null, isGstInclusive: brainGstInclusive = true }: any) => {
        const brainRunContext = captureProjectRunContext(givenContext || {
            projectId: brainProjectId,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });
        const brainRunIsCurrent = () => isProjectRunContextCurrent(brainRunContext, {
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });
        const liveQuote = quoteOverride || quoteDataRef.current || quoteData;
        // On the list's own basis (money review N1): an ex-GST list read every
        // row as hand-priced against its inc receipt and was never repriced.
        const result = repriceBrainScope(brainScope, liveQuote, { isGstInclusive: brainGstInclusive });
        if (!result.ok || !result.changed) {
            if (!quiet) {
                setToast(result.ok
                    ? { message: 'Prices checked with pricing-core: nothing changed.', type: 'success' }
                    : { message: result.reason, type: 'warning' });
            }
            return projectCostingSheetRef.current || null;
        }
        if (!brainRunIsCurrent()) return null;
        const nextQuote = liveQuote && typeof liveQuote === 'object' ? { ...liveQuote, scope: result.scope } : liveQuote;
        let persisted: any = null;
        try {
            persisted = await saveCurrentProject({ scope: result.scope, ...(nextQuote ? { quoteData: nextQuote } : {}) }, { runContext: brainRunContext });
        } catch (error: any) {
            if (error?.name === 'AbortError') return null;
            setToast({ message: 'Pricing-core priced the items again, but the quote could not be saved, so nothing changed.', type: 'error' });
            return null;
        }
        if (!persisted || !brainRunIsCurrent()) return null;
        const savedScope = Array.isArray(persisted.scope) ? persisted.scope : result.scope;
        const savedQuote = persisted.quoteData || persisted.quote_data || nextQuote;
        scopeRef.current = savedScope;
        setScope(savedScope);
        if (savedQuote) {
            quoteDataRef.current = savedQuote;
            setQuoteData(savedQuote);
        }
        const moved = result.moved.map((m) => `${m.title} $${m.from.toLocaleString('en-AU')} to $${m.to.toLocaleString('en-AU')}`).join('; ');
        setToast({ message: `Pricing-core priced ${result.moved.length === 1 ? 'one item' : `${result.moved.length} items`} again after the change: ${moved}.`, type: 'info', duration: 8000 });
        return projectCostingSheetRef.current || null;
    };

    const handleGenerateProjectCostingSheet = async ({ source = 'manual', scopeOverride = null, projectWMSOverride = null, gstInclusiveOverride = undefined, formDataOverride = null, quoteDataOverride = null, expectedProjectId = null, runContext = null, commercialDecisionOwner = null, returnPersistenceReceipt = false, silent = false, throwOnFailure = false }: any = {}) => {
        // A user without live Pricing access never gets commercial workings
        // back: the server strips costingSheet and every costBreakdown from
        // their Project responses and restores the stored ones on save
        // (backend/src/services/project-pricing-projection.js). The automatic
        // refresh therefore can never see its own receipt. Measured
        // 2026-10-09: a member who only opened a job re-saved it every 1-3s.
        if (source === 'auto-scope-change' && canPersistInternalPricing === false) return null;
        const effectiveScope = Array.isArray(scopeOverride) && scopeOverride.length > 0
            ? scopeOverride
            : scope;
        const effectiveGstInclusive = typeof gstInclusiveOverride === 'boolean'
            ? gstInclusiveOverride
            : !!scopeGstInclusive;
        const effectiveFormData = formDataOverride && typeof formDataOverride === 'object'
            ? { ...formData, ...formDataOverride }
            : formData;
        // W4 (Quote Brain DESIGN.md 2.12): a brain project is priced by
        // pricing-core, never by the old costing engines.
        const brainProjectId = expectedProjectId ?? runContext?.projectId ?? currentProjectIdRef.current ?? currentProjectId ?? null;
        if (isBrainProject(brainProjectId, { scope: effectiveScope })) {
            return priceBrainProjectWithPricingCore({ scope: effectiveScope, quoteDataOverride, silent, runContext, expectedProjectId: brainProjectId, isGstInclusive: effectiveGstInclusive });
        }
        // 2026-08-19: read the LIVE wms via projectWMSRef, not the render
        // closure. The auto-scope-change effect below arms its 800ms timer on
        // the scope commit — a render where Stage 3's fresh WMS has NOT yet
        // committed (setProjectWMS lands a separate commit later, and this
        // effect's deps don't include projectWMS, so the timer is never
        // re-armed). The closure's `projectWMS` is then the PREVIOUS record,
        // and the save below persisted fresh-scope + stale-wms — a torn pair
        // that failed isWmsFreshForScope at pipeline-runner.js:1138 and killed
        // every batch job at Stage 3 exit (WMS_SCOPE_STALE). The ref is
        // written synchronously by every WMS producer (use-wms-generation:886,
        // use-scope-morph:374, the WMS edit handlers) and kept in lockstep
        // with state at App.jsx:2555, so it is fresh at timer-fire time.
        const wmsForCosting = projectWMSOverride || projectWMSRef.current || projectWMS;
        if (!Array.isArray(effectiveScope) || effectiveScope.length === 0) {
            const emptyScopeError = new Error('Cannot refresh saved pricing without a priced scope.');
            if (throwOnFailure) throw emptyScopeError;
            if (!silent) {
                setToast({ message: 'Cannot generate Costing Sheet with an empty scope.', type: 'warning' });
            } else {
                console.info('[Costing] Auto-fire skipped: scope empty at fire time (expected mid-analysis-commit).');
            }
            return null;
        }
        const costingRunContext = captureProjectRunContext(runContext || {
            projectId: expectedProjectId ?? currentProjectIdRef.current ?? currentProjectId ?? null,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });
        const costingRunContextIsCurrent = () => isProjectRunContextCurrent(costingRunContext, {
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });
        // Refuse stale work before it can steal the global costing owner from a
        // newer project's in-flight generation.
        if (!costingRunContextIsCurrent()) {
            if (throwOnFailure) throw createStaleProjectRunError('Project costing');
            return null;
        }
        const costingOwner = costingGenerationOwnerRef.current + 1;
        costingGenerationOwnerRef.current = costingOwner;
        const costingOwnsWriteback = () => (
            costingGenerationOwnerRef.current === costingOwner
            && costingRunContextIsCurrent()
        );
        const assertCostingOwnsWriteback = () => {
            if (!costingOwnsWriteback()) throw createStaleProjectRunError('Project costing');
        };
        assertCostingOwnsWriteback();
        setCostingRegenerating(true);
        try {
            const { generateProjectCostingSheet } = await import('../services/project-costing');
            let costingConfig = null;
            let businessPricing = null;
            try {
                const { BusinessPricingService } = await import('../services/pricing');
                // One snapshot owns both costing rules and rate authority for
                // this sheet. Passing only costingConfig made the persisted
                // ratesheet_fingerprint null, so later rate-book edits could
                // not invalidate the real project costing artifact.
                businessPricing = await BusinessPricingService.getDataReadOnly({ failIfMissing: true });
                assertCostingOwnsWriteback();
                costingConfig = businessPricing?.costingConfig || null;
            } catch (cfgErr: any) {
                if (!costingOwnsWriteback() || cfgErr?.name === 'AbortError') {
                    throw createStaleProjectRunError('Project costing');
                }
                // A costing sheet without the full rate-book snapshot cannot
                // carry a ratesheet fingerprint and must not become the live
                // Stage 4/5 artifact. Keep raw detail in developer diagnostics,
                // but never expose internal paths or background refresh errors
                // to the customer.
                console.error('[Costing] Pricing snapshot load failed:', cfgErr?.message || cfgErr);
                if (throwOnFailure) throw cfgErr;
                if (!silent) {
                    setToast({
                        title: 'Internal pricing needs review',
                        message: formatCostingRefreshFailure(cfgErr),
                        type: 'warning',
                    });
                }
                return null;
            }
            assertCostingOwnsWriteback();
            // Bind the QM quote number ASSIGNED to this project into the saved
            // receipt, so the staff export names the job even when the client
            // gave no order number or address. It is read here, in the same
            // synchronous step as the ownership check above, from the guarded
            // override and then the live quote. Those are the project this run
            // owns (assertCostingOwnsWriteback above, and the project-load
            // owner guards that govern quoteDataRef); this read adds no guard of
            // its own and no cross-project race was demonstrated. A quote that
            // says its number, including an explicit clear (null or ''), is
            // authoritative; one that does not mention it (a partial override)
            // defers to the next. No quote loaded (undefined) keeps the previous
            // receipt's number; a loaded quote with none (null) is recorded as
            // none. Nothing here reads the address or the title.
            const assignedQmQuoteNo = resolveAssignedQmQuoteNo([quoteDataOverride, quoteDataRef.current ?? quoteData]);
            const result = generateProjectCostingSheet({
                scopeItems: effectiveScope,
                formData: effectiveFormData,
                qmQuoteNo: assignedQmQuoteNo,
                projectStrategy,
                projectWMS: wmsForCosting,
                overallProgramme: projectWMSOverride?.structured?.programme
                    || wmsForCosting?.structured?.programme
                    || workMethodOverallProgramme,
                previousCostingSheet: projectCostingSheet,
                costingConfig,
                businessPricing,
                businessName: effectiveFormData?.businessName || fallbackBusinessName(),
                gstInclusive: effectiveGstInclusive,
            });
            const sheetRecord = {
                version: result.version,
                generated_at: result.generated_at,
                scope_hash: result.scope_hash,
                // 2026-08-16: these two were RETURNED by the generator
                // (project-costing.js:291 + :302) but dropped here, and the
                // drop is load-bearing in the wrong direction. isCostingStale()
                // reads `if (!costingSheet.pricing_fingerprint) return true`
                // (project-costing.js:323), so a persisted sheet was
                // PERMANENTLY stale whenever a pricingOptions bag was supplied.
                // The Stage 5 staleness banner therefore auto-regenerated on
                // every single project open and each regeneration called
                // saveCurrentProject — the write could never converge because
                // the field that would have settled it was never stored.
                pricing_fingerprint: result.pricing_fingerprint,
                ratesheet_fingerprint: result.ratesheet_fingerprint,
                content_fingerprint: result.content_fingerprint,
                structured: result.structured,
                generated_by: result.generated_by,
                triggered_by: source,
                // A generated in-memory record is never allowed to claim it
                // crossed the project save boundary. The exact record passed
                // to a successful save is promoted below.
                persistence_verified: false,
            };
            // Persist ONLY when the sheet materially changed.
            //
            // The auto-refresh effect below keys on `scope`, and loading a
            // project hands it a brand-new array identity even when the content
            // is byte-identical. So every project open regenerated an IDENTICAL
            // sheet and then paid for a full saveCurrentProject envelope —
            // measured 2026-08-16 at 16.2MB, ~5s after the open, with
            // insideGuard=true. That last part is why nothing caught it: the
            // write lands inside the 2000ms projectLoadGuardUntilRef window,
            // during which the meaningful-change comparator re-baselines and
            // never sets the dirty bit, so this explicit save was the ONE
            // remaining write on the open path.
            //
            // `generated_at` and `triggered_by` are excluded from the comparison
            // because they change on every run by construction and would defeat
            // it. `structured` is the whole computed payload, so a genuine rate
            // or scope change still shows up here and still saves.
            // Compare the scope + pricing receipts AND the stable structured
            // content receipt. The content fingerprint deliberately excludes
            // generated_at, issue version/date and the previous-version note,
            // while covering project identity, HBCF state, programme, actuals
            // and acknowledgements. Comparing raw `structured` would never
            // converge because those issue-only fields move on every run.
            //
            // A legacy sheet stored before the fingerprint fix above has no
            // pricing_fingerprint, so it compares unequal exactly ONCE, saves,
            // and converges. That is the intended migration path.
            const _costingUnchanged = !!projectCostingSheet
                && projectCostingSheet.persistence_verified === true
                && !!sheetRecord.pricing_fingerprint
                && !!sheetRecord.content_fingerprint
                && projectCostingSheet.scope_hash === sheetRecord.scope_hash
                && projectCostingSheet.pricing_fingerprint === sheetRecord.pricing_fingerprint
                && projectCostingSheet.content_fingerprint === sheetRecord.content_fingerprint;
            const persistedSheetRecord = {
                ...sheetRecord,
                persistence_verified: true,
            };
            let costingPersistenceVerified = _costingUnchanged;
            let canonicalPersistedProject = null;
            if (_costingUnchanged) {
                console.info(`[Costing] Regenerated sheet is identical (source=${source}) - skipping redundant project save.`);
            } else {
                assertCostingOwnsWriteback();
                try {
                    const persisted = await saveCurrentProject({
                        // Save project identity/HBCF form state and the receipt
                        // together. A Stage 5 HBCF callback may supply an
                        // override before React has committed setFormData.
                        formData: effectiveFormData,
                        costingSheet: persistedSheetRecord,
                        scope: effectiveScope,
                        ...(quoteDataOverride && typeof quoteDataOverride === 'object'
                            ? { quoteData: quoteDataOverride }
                            : {}),
                        ...(wmsForCosting ? { wms: wmsForCosting } : {}),
                    }, {
                        runContext: costingRunContext,
                        commercialDecisionOwner,
                        beforeCommit: assertCostingOwnsWriteback,
                    });
                    assertCostingOwnsWriteback();
                    costingPersistenceVerified = !!persisted;
                    canonicalPersistedProject = persisted || null;
                } catch (sErr: any) {
                    if (sErr?.name === 'AbortError') throw sErr;
                    console.warn('[Costing] Save after generate failed (non-fatal):', sErr?.message);
                }
            }
            assertCostingOwnsWriteback();
            if (!costingPersistenceVerified) {
                if (throwOnFailure) {
                    const persistenceError: any = new Error('Costings were recalculated but the saved pricing receipt could not be persisted. The previous saved pricing is unchanged.');
                    persistenceError.code = 'COSTING_PERSISTENCE_FAILED';
                    throw persistenceError;
                }
                if (!silent) {
                    setToast({
                        message: 'Costing was generated but could not be saved. Stage 5 will stay locked until the project is saved.',
                        type: 'error',
                    });
                }
                // Retain the last persisted sheet. Installing an unsaved sheet
                // for a proposed scope would tear the UI away from the durable
                // project and could make a failed HBCF decision look applied.
                return null;
            }
            // If an identical regeneration skipped I/O, retain the exact
            // previously saved record. Never install a freshly generated
            // timestamp/project/HBCF projection under somebody else's old
            // persistence receipt.
            const liveSheetRecord = _costingUnchanged
                ? projectCostingSheet
                : persistedSheetRecord;
            // A commercial-decision caller validates and atomically paints the
            // canonical server envelope. Installing this proposal here would
            // tear costing away from scope/form/quote if that receipt failed.
            if (!returnPersistenceReceipt) {
                assertCostingOwnsWriteback();
                setProjectCostingSheet(liveSheetRecord);
                projectCostingSheetRef.current = liveSheetRecord;
            }
            if (!silent) setToast({ message: `Costing Sheet ready (${result.version}).`, type: 'success' });
            return returnPersistenceReceipt
                ? { costingSheet: liveSheetRecord, canonicalProject: canonicalPersistedProject }
                : liveSheetRecord;
        } catch (err: any) {
            if (err?.name === 'AbortError') return null;
            console.error('[Costing] Unexpected error:', err);
            if (throwOnFailure) throw err;
            if (!silent) {
                setToast({
                    title: 'Internal pricing needs review',
                    message: formatCostingRefreshFailure(err),
                    type: 'warning',
                    duration: 8000,
                });
            }
            return null;
        } finally {
            if (costingOwnsWriteback()) {
                setCostingRegenerating(false);
            }
        }
    };

    // HBCF is the strong default, not an immutable commercial decision. Stage
    // 4 and Stage 5 both dispatch through this one atomic boundary so the
    // builder's explicit exclusion/restore, the exact visible scope and the
    // quote snapshot cannot drift apart. Every job, written by QuoteMaker or by
    // hand, takes the same path.
    const handleSetBuilderHbcfDecision = async (next: any, decision: any = {}) => {
        if (isCurrentProjectQuoteGenerationInFlight()) return null;
        if (hbcfDecisionSaveOwnerRef.current !== null) {
            const error: any = new Error('Another HBCF decision is already being saved. Wait for it to finish.');
            error.code = 'PROJECT_COMMERCIAL_DECISION_IN_FLIGHT';
            throw error;
        }
        if (businessPricingSnapshotStatus !== 'ready' || !currentBusinessPricingSnapshot) {
            const error = new Error('Current Pricing data is unavailable. HBCF was not changed; restore Pricing and try again.');
            setToast({ message: error.message, type: 'error' });
            throw error;
        }
        const decisionRunContext = captureProjectRunContext({
            projectId: currentProjectIdRef.current ?? currentProjectId ?? null,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });
        const decisionContextIsCurrent = () => isProjectRunContextCurrent(decisionRunContext, {
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        });

        // Let a save that started before the click drain completely, then claim
        // exclusive ownership synchronously. Once claimed, both the public save
        // wrapper and legacy direct-ref callers reject unrelated envelopes.
        const saveIdleDeadline = Date.now() + 15_000;
        while (isSavingProjectRef.current || pendingSaveRef.current || saveDrainTimerRef.current) {
            if (!decisionContextIsCurrent()) throw createStaleProjectRunError('Builder HBCF decision');
            if (Date.now() >= saveIdleDeadline) {
                const error: any = new Error('QuoteMaker is still saving another change. HBCF was not changed; wait for Save to finish and try again.');
                error.code = 'PROJECT_SAVE_BUSY';
                throw error;
            }
            await new Promise(resolve => setTimeout(resolve, 25));
        }
        if (!decisionContextIsCurrent()) throw createStaleProjectRunError('Builder HBCF decision');
        const commercialDecisionOwner = { kind: 'hbcf', runContext: decisionRunContext };
        claimHbcfDecisionSaveOwner(hbcfDecisionSaveOwnerRef, commercialDecisionOwner);

        try {
            const flag = !!next;
            const sourceScope = Array.isArray(decision?.sourceScope)
                ? decision.sourceScope
                : (Array.isArray(quoteEditorLiveSnapshot?.scope)
                    ? quoteEditorLiveSnapshot.scope
                    : (Array.isArray(scopeRef.current) ? scopeRef.current : scope));
            const costingConfig = currentBusinessPricingSnapshot.costingConfig
                || currentBusinessPricingSnapshot.costing_config
                || {};
            // The business's own Pricing page method, or the starter method;
            // never RJ Bird's card under it (stage4-pricing.ts jobPricingMethodology).
            const methodology = jobPricingMethodology(costingConfig);
            if (!methodology?.hbcf) {
                const error = new Error('Your Pricing page method is missing figures HBCF needs, so HBCF was not changed. Complete the method on the Pricing page and try again.');
                setToast({ message: error.message, type: 'error' });
                throw error;
            }
            // A brain project has no Work Method: its regulated-work posture is
            // the one the Quote Brain priced with.
            const brainCompliance = (quoteDataRef.current || quoteData)?.meta?.quoteBrain?.compliance;
            const brainCompliancePosture = brainCompliance?.regulated === 'yes'
                ? { status: 'confirmed_required', reason: brainCompliance.reason || null }
                : (brainCompliance?.regulated === 'provisional' ? { status: 'provisional_required', reason: brainCompliance.reason || null } : null);
            const compliancePosture = projectWMSRef.current?.structured?.compliance_posture
                || projectWMSRef.current?.structured?.compliancePosture
                || projectWMSRef.current?.compliance_posture
                || projectWMSRef.current?.compliancePosture
                || projectWMS?.structured?.compliance_posture
                || projectWMS?.structured?.compliancePosture
                || projectWMS?.compliance_posture
                || projectWMS?.compliancePosture
                || brainCompliancePosture
                || null;
            // On the job's own GST basis, so an ex-GST Stage 4 gets the same
            // answer as the quote's pricing check (stage4-pricing.ts).
            const statutory = reconcileStatutoryRowsOnScopeBasis(sourceScope, {
                methodology,
                compliancePosture,
                hbcfWaived: flag,
                isGstInclusive: scopeGstInclusive !== false,
            });
            if (String(statutory?.status || '').includes('collision') || !Array.isArray(statutory?.scopeItems)) {
                const error = new Error('HBCF could not be changed because an existing manual statutory row conflicts with the managed HBCF package. Review the scope item first.');
                setToast({ message: error.message, type: 'error' });
                throw error;
            }
            const liveScope = statutory.scopeItems;
            if (flag && liveScope.some(managedScopeItemIncludesHbcf)) {
                const error = new Error('HBCF was not excluded because the reconciled scope still contains a managed HBCF component. Nothing was saved.');
                setToast({ message: error.message, type: 'error' });
                throw error;
            }
            const nextFormData = {
                ...formData,
                hbcfWaived: flag,
                hbcfIncludedInScope: false,
                hbcfWaiverReason: flag
                    ? (decision?.reason || 'Builder deliberately excluded HBCF from this quote.')
                    : null,
                hbcfWaiverConfirmedBy: flag
                    ? (decision?.confirmedBy || formData?.assessor || 'Authenticated QuoteMaker builder')
                    : null,
                hbcfWaiverConfirmedAt: flag
                    ? (decision?.confirmedAt || new Date().toISOString())
                    : null,
                // A builder HBCF decision settles the job's HBCF first run
                // (stage4-pricing.ts fastTrackFirstRunNeedsHbcf).
                hbcfFirstRunDecidedAt: formData?.hbcfFirstRunDecidedAt || new Date().toISOString(),
            };
            const liveQuote = decision?.quoteSnapshot
                || (quoteEditorOpen ? quoteEditorLiveSnapshot : null)
                || quoteDataRef.current
                || quoteData;
            // Every job: the statutory step above is the whole change. The rows
            // go into both stages as one list, saved with the form in one write.
            let nextQuoteData = liveQuote && typeof liveQuote === 'object'
                ? { ...liveQuote, scope: liveScope }
                : liveQuote;
            if (nextQuoteData && typeof nextQuoteData === 'object') {
                const bound = bindGeneratedQuoteToCurrentScope({
                    scope: liveScope,
                    quoteData: nextQuoteData,
                });
                nextQuoteData = bound.project?.quoteData || nextQuoteData;
            }
            // The HBCF rows change the items the Work Method was projected
            // onto. A Work Method that was fresh for the items before stays
            // fresh, saved in the same write; a stale one stays stale (review
            // round 10: Add HBCF left the quote it was pressed for refused).
            const decisionWms = projectWMSRef.current;
            const reboundWms = decisionWms && hasFreshWmsScopeProjection(decisionWms, sourceScope)
                ? bindWmsToScopeProjection(decisionWms, liveScope, { source: flag ? 'hbcf-builder-exclusion' : 'hbcf-default-restore' })
                : null;
            let canonicalProject: any = null;
            try {
                canonicalProject = await saveCurrentProject({
                    scope: liveScope,
                    formData: nextFormData,
                    ...(nextQuoteData && typeof nextQuoteData === 'object' ? { quoteData: nextQuoteData } : {}),
                    ...(reboundWms ? { wms: reboundWms } : {}),
                }, { runContext: decisionRunContext, commercialDecisionOwner });
            } catch (error: any) {
                if (error?.name === 'AbortError') throw error;
                canonicalProject = null;
            }
            if (!canonicalProject || !decisionContextIsCurrent()) {
                const error = new Error('HBCF was not changed because the quote could not be saved.');
                setToast({ message: error.message, type: 'error' });
                throw error;
            }
            let canonicalReceipt;
            try {
                canonicalReceipt = assertCanonicalHbcfDecisionReceipt({
                    canonicalProject,
                    proposedScope: liveScope,
                    proposedFormData: nextFormData,
                    proposedQuoteData: nextQuoteData,
                });
            } catch (error: any) {
                setToast({ message: error.message, type: 'error' });
                throw error;
            }
            const {
                scope: canonicalScope,
                formData: canonicalFormData,
                quoteData: canonicalQuoteData,
            } = canonicalReceipt;
            if (flag && canonicalScope.some(managedScopeItemIncludesHbcf)) {
                const error = new Error('HBCF exclusion was not accepted because the saved quote still contains a managed HBCF component.');
                setToast({ message: error.message, type: 'error' });
                throw error;
            }
            // Paint only the canonical server response. Cloud media sanitation
            // can replace session-only bytes during save, so repainting the
            // proposal would reintroduce an unsaveable local quote until reload.
            scopeRef.current = canonicalScope;
            setScope(canonicalScope);
            setFormData(canonicalFormData);
            if (reboundWms) {
                const savedWms = canonicalProject?.wms && typeof canonicalProject.wms === 'object' ? canonicalProject.wms : reboundWms;
                projectWMSRef.current = savedWms;
                setProjectWMS(savedWms);
            }
            if (canonicalQuoteData) {
                quoteDataRef.current = canonicalQuoteData;
                setQuoteData(canonicalQuoteData);
            }
            return {
                scope: canonicalScope,
                formData: canonicalFormData,
                quoteData: canonicalQuoteData,
            };
        } finally {
            if (hbcfDecisionSaveOwnerRef.current === commercialDecisionOwner) {
                hbcfDecisionSaveOwnerRef.current = null;
            }
        }
    };

    return {
        handleGenerateProjectCostingSheet,
        handleSetBuilderHbcfDecision,
    };
}
